import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_BASE = process.env.REACT_APP_API_URL;


export default function QualityDocumentDetail() {
  const { id, revisionId } = useParams();
  const { getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();

  const role = String(user?.publicMetadata?.role || '').toLowerCase().trim();
  const isStaff = ['admin', 'quality-manager', 'production-engineer', 'service'].includes(role);
  const supplierKey = ['gpv','ubiqconn','acmepoint','hk_ringer'].includes(role.replace(/^test-/, ''));

  const [doc, setDoc] = useState(null);
  const [revision, setRevision] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [working, setWorking] = useState(false);
  const [showObsolete, setShowObsolete] = useState(false);
  const [obsoleteForm, setObsoleteForm] = useState({ reason: '', date: '', replacementId: '' });
  const [acknowledging, setAcknowledging] = useState(false);

  const apiCall = useCallback(async (path, method = 'GET', body = null) => {
    const token = await getToken();
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    return res.json();
  }, [getToken]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const docData = await apiCall(`/quality/documents/${id}`);
    if (!docData.ok) { setError(docData.message || 'Not found'); setLoading(false); return; }
    setDoc(docData.document);
    if (!revisionId && isStaff && docData.document.movedToChangeControl?.recordId) {
      navigate(`/quality/change-control/${docData.document.movedToChangeControl.recordId}`, { replace: true });
      return;
    }

    if (revisionId) {
      const revData = await apiCall(`/quality/documents/${id}/revisions/${revisionId}`);
      if (!revData.ok) { setError('This exact historical revision is unavailable.'); setLoading(false); return; }
      setRevision(revData.revision);
    } else {
      setRevision(docData.currentRevision);
    }

    const revsData = await apiCall(`/quality/documents/${id}/revisions`);
    setRevisions(revsData.ok ? revsData.revisions : []);
    setLoading(false);
  }, [apiCall, id, revisionId, isStaff, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleSubmitReview = async () => {
    if (!revision) return;
    setWorking(true); setActionError('');
    try {
      const d = await apiCall(`/quality/documents/${id}/revisions/${revision._id}/submit-review`, 'POST');
      if (d.ok) load();
      else setActionError(d.message || d.error || 'Failed');
    } catch (err) {
      setActionError(err?.message || 'Request failed');
    } finally {
      setWorking(false);
    }
  };

  const handleRelease = async () => {
    if (!revision) return;
    setWorking(true); setActionError('');
    try {
      const d = await apiCall(`/quality/documents/${id}/revisions/${revision._id}/release`, 'POST');
      if (d.ok) load();
      else setActionError(d.message || d.error || 'Failed');
    } catch (err) {
      setActionError(err?.message || 'Request failed');
    } finally {
      setWorking(false);
    }
  };

  const handleNewRevision = async () => {
    setWorking(true); setActionError('');
    try {
      const d = await apiCall(`/quality/documents/${id}/revisions`, 'POST', { changeSummary: '' });
      if (d.ok) navigate(`/quality/documents/${id}/edit-draft`);
      else setActionError(d.message || d.error || 'Failed');
    } catch (err) {
      setActionError(err?.message || 'Request failed');
    } finally {
      setWorking(false);
    }
  };

  const handleObsolete = async () => {
    if (!obsoleteForm.reason.trim()) { setActionError('Reason is required'); return; }
    setWorking(true); setActionError('');
    try {
      const d = await apiCall(`/quality/documents/${id}/request-obsolete`, 'POST', {
        obsoleteReason: obsoleteForm.reason,
        obsoleteDate: obsoleteForm.date || null,
        replacementDocumentId: obsoleteForm.replacementId || null,
      });
      if (d.ok) { setShowObsolete(false); load(); }
      else setActionError(d.message || d.error || 'Failed');
    } catch (err) {
      setActionError(err?.message || 'Request failed');
    } finally {
      setWorking(false);
    }
  };

  if (loading) return <div style={{ color: 'var(--ni-text-muted)', padding: 40 }}>Loading...</div>;
  if (error) return <div style={{ color: '#EC2124', padding: 40 }}>{error}</div>;
  if (!doc) return null;

  const moved = !!doc.movedToChangeControl;
  const canManage = isStaff && !moved;
  const canRelease = canManage;

  const isDraft = revision?.status === 'Draft' || revision?.status === 'In Review';
  const isReleased = revision?.status === 'Released';
  const isObsolete = doc.currentStatus === 'Obsolete';
  // Staff manage anything; a supplier may iterate a doc they created OR one shared
  // with them (visibility.suppliers) — they revise a Released doc into a new draft
  // and submit it for NI approval. The backend enforces the precise per-revision rule.
  const mySupplierKey = supplierKey ? String(role || '').replace(/^test-/, '') : null;
  const canContributeThis = !moved && (canManage || (supplierKey
    && (doc.createdBy === user?.id || (mySupplierKey && (doc.visibility?.suppliers || []).includes(mySupplierKey)))));

  return (
    <div>
      {moved && <div className="ni-panel" style={{ marginBottom: 20 }}>
        <strong>Historical Quality Document</strong>
        <p>The content and revision history are preserved in Change Control. This original view is read-only.</p>
        {isStaff && doc.movedToChangeControl.recordId && <button className="ni-button"
          onClick={() => navigate(`/quality/change-control/${doc.movedToChangeControl.recordId}`)}>Open current change record</button>}
      </div>}
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <button
          onClick={() => navigate('/quality/documents')}
          style={{ background: 'none', border: 'none', color: 'var(--ni-text-muted)', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 12 }}
        >
          ← Library
        </button>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--ni-green)', marginBottom: 4 }}>{doc.documentNumber}</div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>{doc.title}</h2>
            <div style={{ marginTop: 8, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <StatusBadge status={doc.currentStatus} />
              {revision && <span style={{ color: 'var(--ni-text-muted)', fontSize: 13 }}>Rev. {revision.revision}</span>}
              {doc.productFamily && <span style={{ color: 'var(--ni-text-muted)', fontSize: 12 }}>{doc.productFamily}</span>}
              {doc.owner && <span style={{ color: 'var(--ni-text-muted)', fontSize: 12 }}>Owner: {doc.owner}</span>}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {canContributeThis && isDraft && !isObsolete && (
              <button onClick={() => navigate(`/quality/documents/${id}/edit-draft`)} style={btnStyle('#46BB89', '#000')}>
                Edit Draft
              </button>
            )}
            {canContributeThis && revision?.status === 'Draft' && (
              <button onClick={handleSubmitReview} disabled={working} style={btnStyle('#60a5fa', '#000')}>
                Submit for Review
              </button>
            )}
            {canRelease && (revision?.status === 'Draft' || revision?.status === 'In Review') && (
              <button onClick={handleRelease} disabled={working} style={btnStyle('#46BB89', '#000')}>
                Release
              </button>
            )}
            {canContributeThis && isReleased && !isObsolete && (
              <button onClick={handleNewRevision} disabled={working} style={btnStyle('#a78bfa', '#000')}>
                New Revision
              </button>
            )}
            {canRelease && !isObsolete && isReleased && (
              <button onClick={() => setShowObsolete(true)} style={btnStyle('#EC2124', '#fff')}>
                Obsolete
              </button>
            )}
          </div>
        </div>

        {actionError && (
          <div style={{ marginTop: 12, color: '#EC2124', fontSize: 13, background: '#EC212422', borderRadius: 6, padding: '8px 12px' }}>
            {actionError}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: 24, alignItems: 'start' }}>
        {/* Main content */}
        <div>
          {revision?.markdownContent ? (
            <div style={{
              background: 'var(--ni-panel)',
              border: '1px solid var(--ni-border-soft)',
              borderRadius: 10,
              padding: '32px 40px',
            }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={mdComponents}
              >
                {revision.markdownContent}
              </ReactMarkdown>
            </div>
          ) : (
            <div style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 10, padding: 40, color: 'var(--ni-text-muted)', textAlign: 'center' }}>
              No content yet.
              {canManage && isDraft && (
                <> <button onClick={() => navigate(`/quality/documents/${id}/edit-draft`)} style={{ background: 'none', border: 'none', color: 'var(--ni-green)', cursor: 'pointer', fontSize: 14 }}>Open editor.</button></>
              )}
            </div>
          )}
          {revision && (
            <RequirementsPanel
              docId={id}
              revision={revision}
              apiCall={apiCall}
              canManage={canManage}
            />
          )}
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Revision history */}
          <div style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 10, padding: '16px' }}>
            <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, color: 'var(--ni-text-muted)' }}>Revisions</div>
            {revisions.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--ni-text-muted)' }}>No revisions</div>
            ) : (
              revisions.map(r => {
                const isActive = String(r._id) === String(revision?._id);
                const displayDate = r.releaseDate || r.createdAt;
                return (
                  <div
                    key={r._id}
                    onClick={() => navigate(`/quality/documents/${id}/revisions/${r._id}`)}
                    style={{
                      cursor: 'pointer',
                      padding: '9px 10px',
                      borderRadius: 6,
                      marginBottom: 4,
                      background: isActive ? 'rgba(70,187,137,0.1)' : 'transparent',
                      borderLeft: isActive ? '2px solid var(--ni-green)' : '2px solid transparent',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: isActive ? 'var(--ni-green)' : 'var(--ni-text)' }}>
                        Rev. {r.revision}
                      </span>
                      <StatusBadge status={r.status} small />
                    </div>
                    {displayDate && (
                      <div style={{ fontSize: 10, color: 'var(--ni-text-muted)' }}>
                        {new Date(displayDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                      </div>
                    )}
                    {(r.approvedByName || r.submittedByName || r.createdByName) && (
                      <div style={{ fontSize: 10, color: 'var(--ni-text-muted)', marginTop: 1 }}>
                        {r.approvedByName
                          ? `Released by ${r.approvedByName}`
                          : r.submittedByName
                          ? `In review — ${r.submittedByName}`
                          : `Created by ${r.createdByName}`}
                      </div>
                    )}
                    {r.changeSummary && (
                      <div style={{ fontSize: 11, color: 'var(--ni-text-muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.changeSummary}>
                        {r.changeSummary}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Metadata */}
          <div style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 10, padding: '16px' }}>
            <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, color: 'var(--ni-text-muted)' }}>Details</div>
            <MetaRow label="Level" value={doc.level} />
            <MetaRow label="Type" value={doc.documentType} />
            {doc.productFamily && <MetaRow label="Family" value={doc.productFamily} />}
            {doc.productNumbers?.length > 0 && <MetaRow label="Products" value={doc.productNumbers.join(', ')} />}
            {doc.suppliers?.length > 0 && <MetaRow label="Suppliers" value={doc.suppliers.join(', ')} />}
            {doc.effectiveDate && <MetaRow label="Effective" value={new Date(doc.effectiveDate).toLocaleDateString()} />}
            {doc.source?.url && <SourceRow source={doc.source} />}
          </div>

          {/* Document control (ISO 9001 §7.5) */}
          <DocumentControlCard doc={doc} canManage={canManage} apiCall={apiCall} onSaved={load} />

          {/* Revision lifecycle trail */}
          {revision && (
            <div style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 10, padding: '16px' }}>
              <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14, color: 'var(--ni-text-muted)' }}>
                Rev. {revision.revision} — Lifecycle
              </div>
              <LifecycleStep
                label="Draft created"
                name={revision.createdByName}
                date={revision.createdAt}
                done
              />
              <LifecycleStep
                label="Submitted for review"
                name={revision.submittedByName}
                date={revision.submittedAt}
                done={!!revision.submittedByName}
              />
              <LifecycleStep
                label="Released"
                name={revision.approvedByName}
                date={revision.releaseDate}
                done={!!revision.approvedByName}
                last
              />
              {revision.changeSummary && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--ni-border-soft)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)', marginBottom: 3 }}>Change summary</div>
                  <div style={{ fontSize: 12, color: 'var(--ni-text-soft)', lineHeight: 1.5 }}>{revision.changeSummary}</div>
                </div>
              )}
              {isObsolete && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--ni-border-soft)' }}>
                  {doc.obsoleteDate && <MetaRow label="Obsoleted" value={new Date(doc.obsoleteDate).toLocaleDateString()} />}
                  {doc.obsoleteReason && <MetaRow label="Reason" value={doc.obsoleteReason} />}
                  {doc.replacementDocumentId && <MetaRow label="Replaced by" value={doc.replacementDocumentId} />}
                </div>
              )}
            </div>
          )}
        {/* Acknowledgement section — Released docs only */}
        {isReleased && !isObsolete && !moved && (() => {
          const acks = doc.acknowledgements || [];
          const myAck = acks.find(a => a.userId === user?.id);
          const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.id || '';
          const handleAck = async () => {
            setAcknowledging(true);
            try {
              const token = await getToken();
              const res = await window.fetch(`${API_BASE}/quality/documents/${id}/acknowledge`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ userName }),
              });
              const data = await res.json();
              if (data.ok) setDoc(data.document);
            } catch {} finally { setAcknowledging(false); }
          };
          return (
            <div style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', borderRadius:10, padding:16 }}>
              <div style={{ fontWeight:700, fontSize:12, textTransform:'uppercase', letterSpacing:'0.06em',
                marginBottom:12, color:'var(--ni-text-muted)' }}>Read & Acknowledged</div>
              {myAck ? (
                <div style={{ display:'flex', alignItems:'center', gap:6, color:'#46BB89', fontSize:13, fontWeight:600, marginBottom: acks.length > 1 ? 10 : 0 }}>
                  ✓ You acknowledged this on {new Date(myAck.acknowledgedAt).toLocaleDateString()}
                </div>
              ) : (
                <button onClick={handleAck} disabled={acknowledging}
                  style={{ background:'#46BB8922', border:'1px solid #46BB8944', color:'#46BB89',
                    borderRadius:6, padding:'7px 14px', fontSize:12, fontWeight:700, cursor:'pointer',
                    marginBottom: acks.length > 0 ? 10 : 0 }}>
                  {acknowledging ? 'Saving…' : '✓ Mark as Read'}
                </button>
              )}
              {acks.length > 0 && (
                <div style={{ fontSize:12, color:'var(--ni-text-muted)' }}>
                  {acks.map((a, i) => (
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'3px 0',
                      borderTop: i > 0 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                      <span style={{ color: a.userId === user?.id ? '#46BB89' : 'var(--ni-text)' }}>
                        {a.userName || a.userId}
                      </span>
                      <span>{new Date(a.acknowledgedAt).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
        </div>
      </div>

      {/* Obsolete modal */}
      {showObsolete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 12, padding: 32, width: 480, maxWidth: '90vw' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 17, fontWeight: 800 }}>Obsolete Document</h3>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)', marginBottom: 6 }}>Reason *</label>
              <textarea
                value={obsoleteForm.reason}
                onChange={e => setObsoleteForm(f => ({ ...f, reason: e.target.value }))}
                style={{ width: '100%', background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '8px 12px', fontSize: 13, minHeight: 80, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)', marginBottom: 6 }}>Obsolete Date</label>
              <input type="date" value={obsoleteForm.date} onChange={e => setObsoleteForm(f => ({ ...f, date: e.target.value }))}
                style={{ width: '100%', background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)', marginBottom: 6 }}>Replaced By (document number, optional)</label>
              <input
                type="text"
                placeholder="e.g. QP-0042"
                value={obsoleteForm.replacementId}
                onChange={e => setObsoleteForm(f => ({ ...f, replacementId: e.target.value }))}
                style={{ width: '100%', background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }}
              />
            </div>
            {actionError && <div style={{ color: '#EC2124', fontSize: 13, marginBottom: 12 }}>{actionError}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleObsolete} disabled={working} style={btnStyle('#EC2124', '#fff')}>Confirm Obsolete</button>
              <button onClick={() => { setShowObsolete(false); setActionError(''); }} style={{ background: 'none', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text-muted)', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function btnStyle(bg, color) {
  return {
    background: bg, color, border: 'none', borderRadius: 6,
    padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
  };
}

function StatusBadge({ status, small }) {
  const statusColors = { Draft: '#94a3b8', 'In Review': '#60a5fa', Released: '#46BB89', Superseded: '#f59e0b', Obsolete: '#EC2124', Archived: '#64748b' };
  const color = statusColors[status] || '#666';
  return (
    <span style={{
      display: 'inline-block', padding: small ? '1px 7px' : '2px 10px', borderRadius: 20,
      fontSize: small ? 10 : 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      background: color + '22', color,
    }}>
      {status}
    </span>
  );
}

function LifecycleStep({ label, name, date, done, last }) {
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: last ? 0 : 0 }}>
      {/* Timeline spine */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16, flexShrink: 0 }}>
        <div style={{
          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
          background: done ? 'var(--ni-green)' : 'var(--ni-border-soft)',
          border: done ? 'none' : '1px solid var(--ni-border-soft)',
          marginTop: 2,
        }} />
        {!last && (
          <div style={{ width: 1, flex: 1, minHeight: 14, background: done ? 'rgba(70,187,137,0.3)' : 'var(--ni-border-soft)', margin: '3px 0' }} />
        )}
      </div>
      {/* Content */}
      <div style={{ paddingBottom: last ? 0 : 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: done ? 'var(--ni-text)' : 'var(--ni-text-muted)', letterSpacing: '0.01em' }}>{label}</div>
        {done && name && <div style={{ fontSize: 11, color: 'var(--ni-green)', marginTop: 1 }}>{name}</div>}
        {done && date && <div style={{ fontSize: 10, color: 'var(--ni-text-muted)', marginTop: 1 }}>{new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</div>}
        {!done && <div style={{ fontSize: 10, color: 'var(--ni-text-muted)', marginTop: 1 }}>Pending</div>}
      </div>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)' }}>{label}</div>
      <div style={{ fontSize: 12, color: 'var(--ni-text-soft)', marginTop: 2, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function SourceRow({ source }) {
  const modified = source.externalModifiedAt ? new Date(source.externalModifiedAt).toLocaleDateString() : null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)' }}>Source of record</div>
      <a href={source.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--ni-green)', marginTop: 2, wordBreak: 'break-word', textDecoration: 'none', display: 'inline-block' }}>
        {(source.system || 'SharePoint')}{source.fileName ? ` — ${source.fileName}` : ''} ↗
      </a>
      {modified && <div style={{ fontSize: 10, color: 'var(--ni-text-muted)', marginTop: 1 }}>Updated {modified}</div>}
    </div>
  );
}

const CLASSIFICATION_COLORS = { Public: '#46BB89', Internal: '#60a5fa', Confidential: '#f59e0b', Restricted: '#EC2124' };

// ISO 9001 §7.5 document-control metadata — display + a self-contained staff edit
// (PATCH /quality/documents/:id) so classification / retention / distribution /
// retrieval keywords / authority sign-off can be maintained over the doc's life.
function DocumentControlCard({ doc, canManage, apiCall, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const d2 = v => (v ? String(v).slice(0, 10) : '');
  const blank = () => ({
    classification: doc.classification || 'Internal',
    controlledCopy: doc.controlledCopy !== false,
    retentionPeriod: doc.retentionPeriod || '',
    retentionNotes: doc.retentionNotes || '',
    distributionNotes: doc.distributionNotes || '',
    keywords: (doc.keywords || []).join(', '),
    reviewIntervalMonths: doc.reviewIntervalMonths || '',
    approval: {
      preparedByName: doc.approval?.preparedByName || '', preparedDate: d2(doc.approval?.preparedDate),
      reviewedByName: doc.approval?.reviewedByName || '', reviewedDate: d2(doc.approval?.reviewedDate),
      approvedByName: doc.approval?.approvedByName || '', approvedDate: d2(doc.approval?.approvedDate),
    },
  });
  const [form, setForm] = useState(blank);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setAppr = (k, v) => setForm(f => ({ ...f, approval: { ...f.approval, [k]: v } }));

  const startEdit = () => { setForm(blank()); setErr(''); setEditing(true); };
  const save = async () => {
    setSaving(true); setErr('');
    try {
      const body = {
        classification: form.classification,
        controlledCopy: form.controlledCopy,
        retentionPeriod: form.retentionPeriod,
        retentionNotes: form.retentionNotes,
        distributionNotes: form.distributionNotes,
        keywords: form.keywords ? form.keywords.split(',').map(s => s.trim()).filter(Boolean) : [],
        reviewIntervalMonths: form.reviewIntervalMonths ? Number(form.reviewIntervalMonths) : null,
        approval: {
          preparedByName: form.approval.preparedByName, preparedDate: form.approval.preparedDate || null,
          reviewedByName: form.approval.reviewedByName, reviewedDate: form.approval.reviewedDate || null,
          approvedByName: form.approval.approvedByName, approvedDate: form.approval.approvedDate || null,
        },
      };
      const r = await apiCall(`/quality/documents/${doc._id}`, 'PATCH', body);
      if (r.ok) { setEditing(false); if (onSaved) onSaved(); }
      else setErr(r.message || r.error || 'Save failed');
    } catch (e) { setErr(e?.message || 'Request failed'); } finally { setSaving(false); }
  };

  const inp = { background: 'var(--ni-panel-raised, #1D2522)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '6px 9px', fontSize: 12, width: '100%', boxSizing: 'border-box' };
  const lbl = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)', marginBottom: 3 };
  const cls = doc.classification || 'Internal';
  const appr = doc.approval || {};
  const hasAppr = appr.preparedByName || appr.reviewedByName || appr.approvedByName;
  const sig = (name, date) => name ? `${name}${date ? ` · ${new Date(date).toLocaleDateString()}` : ''}` : '—';

  return (
    <div style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 10, padding: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)' }}>Document Control</div>
        {canManage && !editing && (
          <button onClick={startEdit} style={{ background: 'none', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text-muted)', borderRadius: 6, padding: '2px 10px', fontSize: 11, cursor: 'pointer' }}>Edit</button>
        )}
      </div>

      {!editing ? (
        <>
          <div style={{ marginBottom: 10 }}>
            <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', background: (CLASSIFICATION_COLORS[cls] || '#666') + '22', color: CLASSIFICATION_COLORS[cls] || '#666' }}>
              {cls}{doc.controlledCopy === false ? ' · Uncontrolled' : ' · Controlled'}
            </span>
          </div>
          {(doc.retentionPeriod || doc.retentionNotes) && <MetaRow label="Retention" value={[doc.retentionPeriod, doc.retentionNotes].filter(Boolean).join(' — ')} />}
          {doc.reviewIntervalMonths ? <MetaRow label="Review every" value={`${doc.reviewIntervalMonths} months`} /> : null}
          {doc.nextReviewDue && <MetaRow label="Next review due" value={new Date(doc.nextReviewDue).toLocaleDateString()} />}
          {doc.distributionNotes && <MetaRow label="Distribution" value={doc.distributionNotes} />}
          {doc.keywords?.length > 0 && <MetaRow label="Keywords" value={doc.keywords.join(', ')} />}
          {hasAppr && (
            <div style={{ marginTop: 8, paddingTop: 10, borderTop: '1px solid var(--ni-border-soft)' }}>
              <div style={{ ...lbl, marginBottom: 6 }}>Authority sign-off</div>
              <MetaRow label="Prepared" value={sig(appr.preparedByName, appr.preparedDate)} />
              <MetaRow label="Reviewed" value={sig(appr.reviewedByName, appr.reviewedDate)} />
              <MetaRow label="Approved" value={sig(appr.approvedByName, appr.approvedDate)} />
            </div>
          )}
        </>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div><div style={lbl}>Classification</div>
              <select style={inp} value={form.classification} onChange={e => set('classification', e.target.value)}>
                {['Public', 'Internal', 'Confidential', 'Restricted'].map(c => <option key={c}>{c}</option>)}
              </select></div>
            <div><div style={lbl}>Review interval (months)</div>
              <input style={inp} type="number" value={form.reviewIntervalMonths} onChange={e => set('reviewIntervalMonths', e.target.value)} /></div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.controlledCopy} onChange={e => set('controlledCopy', e.target.checked)} /> Controlled copy
          </label>
          <div><div style={lbl}>Retention period</div>
            <input style={inp} value={form.retentionPeriod} onChange={e => set('retentionPeriod', e.target.value)} placeholder="e.g. 7 years after obsolescence" /></div>
          <div><div style={lbl}>Retention notes</div>
            <input style={inp} value={form.retentionNotes} onChange={e => set('retentionNotes', e.target.value)} /></div>
          <div><div style={lbl}>Distribution / access notes</div>
            <input style={inp} value={form.distributionNotes} onChange={e => set('distributionNotes', e.target.value)} /></div>
          <div><div style={lbl}>Keywords (comma-separated)</div>
            <input style={inp} value={form.keywords} onChange={e => set('keywords', e.target.value)} /></div>
          <div style={{ paddingTop: 6, borderTop: '1px solid var(--ni-border-soft)' }}>
            <div style={{ ...lbl, marginBottom: 6 }}>Authority sign-off</div>
            {[['preparedByName', 'preparedDate', 'Prepared'], ['reviewedByName', 'reviewedDate', 'Reviewed'], ['approvedByName', 'approvedDate', 'Approved']].map(([nk, dk, label]) => (
              <div key={nk} style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 8, marginBottom: 6 }}>
                <input style={inp} placeholder={label} value={form.approval[nk]} onChange={e => setAppr(nk, e.target.value)} />
                <input style={inp} type="date" value={form.approval[dk]} onChange={e => setAppr(dk, e.target.value)} />
              </div>
            ))}
          </div>
          {err && <div style={{ color: '#EC2124', fontSize: 12 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={save} disabled={saving} style={{ background: 'var(--ni-green)', color: '#000', border: 'none', borderRadius: 6, padding: '6px 16px', fontWeight: 700, fontSize: 12, cursor: saving ? 'wait' : 'pointer' }}>{saving ? 'Saving…' : 'Save'}</button>
            <button onClick={() => setEditing(false)} style={{ background: 'transparent', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

const mdComponents = {
  h1: ({ children }) => <h1 style={{ fontSize: 22, fontWeight: 800, borderBottom: '1px solid var(--ni-border-soft)', paddingBottom: 10, marginTop: 0 }}>{children}</h1>,
  h2: ({ children }) => <h2 style={{ fontSize: 17, fontWeight: 700, marginTop: 28, marginBottom: 10, color: 'var(--ni-text)' }}>{children}</h2>,
  h3: ({ children }) => <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 20, color: 'var(--ni-text-soft)' }}>{children}</h3>,
  p: ({ children }) => <p style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--ni-text-soft)', margin: '0 0 12px' }}>{children}</p>,
  table: ({ children }) => <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>{children}</table>,
  th: ({ children }) => <th style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.2)', textAlign: 'left', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', border: '1px solid var(--ni-border-soft)' }}>{children}</th>,
  td: ({ children }) => <td style={{ padding: '8px 12px', border: '1px solid var(--ni-border-soft)', fontSize: 13 }}>{children}</td>,
  code: ({ children }) => <code style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 4, padding: '2px 6px', fontSize: 12, fontFamily: 'monospace', color: 'var(--ni-green)' }}>{children}</code>,
  ul: ({ children }) => <ul style={{ paddingLeft: 20, marginBottom: 12 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ paddingLeft: 20, marginBottom: 12 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 4, fontSize: 14, color: 'var(--ni-text-soft)' }}>{children}</li>,
  hr: () => <hr style={{ border: 'none', borderTop: '1px solid var(--ni-border-soft)', margin: '20px 0' }} />,
  strong: ({ children }) => <strong style={{ fontWeight: 700, color: 'var(--ni-text)' }}>{children}</strong>,
};

const EMPTY_REQ_FORM = {
  title: '', requirementText: '', requirementType: 'manual',
  severity: 'Major', sectionNumber: '', sectionTitle: '', requiresEvidence: false,
};

const SEVERITY_COLORS = { Critical: '#EC2124', Major: '#f59e0b', Minor: '#60a5fa' };
const TYPE_LABELS = { manual: 'Manual check', measurement: 'Measurement', checklist: 'Checklist', evidence: 'Evidence required' };

function RequirementsPanel({ docId, revision, apiCall, canManage }) {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_REQ_FORM });
  const [saving, setSaving] = useState(false);
  const [reqError, setReqError] = useState('');

  const canEdit = canManage && revision?.status === 'Draft';

  useEffect(() => {
    if (!revision?._id) return;
    setLoading(true);
    apiCall(`/quality/documents/${docId}/revisions/${revision._id}/requirements`)
      .then(d => { if (d?.ok) setRequirements(d.requirements || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [revision?._id]); // eslint-disable-line

  const resetForm = () => setForm({ ...EMPTY_REQ_FORM });

  const handleSave = async () => {
    if (!form.title.trim()) { setReqError('Title is required'); return; }
    setSaving(true); setReqError('');
    try {
      const d = editId
        ? await apiCall(`/quality/requirements/${editId}`, 'PATCH', form)
        : await apiCall(`/quality/documents/${docId}/revisions/${revision._id}/requirements`, 'POST', form);
      if (d?.ok) {
        if (editId) {
          setRequirements(r => r.map(x => String(x._id) === editId ? d.requirement : x));
        } else {
          setRequirements(r => [...r, d.requirement]);
        }
        resetForm(); setShowAdd(false); setEditId(null);
      } else { setReqError(d?.message || d?.error || 'Failed'); }
    } catch (err) { setReqError(err?.message || 'Request failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (req) => {
    if (!window.confirm(`Delete requirement "${req.title}"?`)) return;
    const d = await apiCall(`/quality/requirements/${req._id}`, 'DELETE');
    if (d?.ok) setRequirements(r => r.filter(x => String(x._id) !== String(req._id)));
  };

  const startEdit = (req) => {
    setForm({
      title: req.title, requirementText: req.requirementText || '',
      requirementType: req.requirementType, severity: req.severity,
      sectionNumber: req.sectionNumber || '', sectionTitle: req.sectionTitle || '',
      requiresEvidence: req.requiresEvidence || false,
    });
    setEditId(String(req._id));
    setShowAdd(true);
  };

  return (
    <div style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 10, padding: '24px 32px', marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15 }}>Requirements</div>
          <div style={{ fontSize: 12, color: 'var(--ni-text-muted)', marginTop: 2 }}>
            {loading ? 'Loading…' : `${requirements.length} requirement${requirements.length !== 1 ? 's' : ''} — evaluated when this doc is linked to an NCR`}
          </div>
        </div>
        {canEdit && (
          <button onClick={() => { resetForm(); setEditId(null); setShowAdd(true); }} style={btnStyle('#46BB89', '#000')}>
            + Add
          </button>
        )}
      </div>

      {!loading && requirements.length === 0 && (
        <div style={{ color: 'var(--ni-text-muted)', fontSize: 13, padding: '8px 0' }}>
          No requirements defined yet.{canEdit && ' Define requirements to enable structured pass/fail evaluation on NCRs that link this document.'}
        </div>
      )}

      {!loading && requirements.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {requirements.map(req => (
            <div key={req._id} style={{ background: 'rgba(0,0,0,0.15)', border: '1px solid var(--ni-border-soft)', borderRadius: 8, padding: '10px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--ni-text-muted)' }}>{req.requirementId}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: SEVERITY_COLORS[req.severity] || '#939597' }}>{req.severity}</span>
                    <span style={{ fontSize: 11, color: 'var(--ni-text-muted)', background: 'rgba(255,255,255,0.05)', padding: '1px 7px', borderRadius: 4 }}>
                      {TYPE_LABELS[req.requirementType] || req.requirementType}
                    </span>
                    {req.requiresEvidence && <span style={{ fontSize: 10, color: '#60a5fa' }}>Evidence required</span>}
                  </div>
                  {(req.sectionNumber || req.sectionTitle) && (
                    <div style={{ fontSize: 11, color: 'var(--ni-text-muted)', marginBottom: 3 }}>
                      {[req.sectionNumber, req.sectionTitle].filter(Boolean).join(' — ')}
                    </div>
                  )}
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ni-text)' }}>{req.title}</div>
                  {req.requirementText && (
                    <div style={{ fontSize: 12, color: 'var(--ni-text-soft)', marginTop: 4, lineHeight: 1.5 }}>{req.requirementText}</div>
                  )}
                </div>
                {canEdit && (
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <button onClick={() => startEdit(req)} style={{ background: 'none', border: 'none', color: 'var(--ni-text-muted)', fontSize: 12, cursor: 'pointer', padding: '3px 8px' }}>Edit</button>
                    <button onClick={() => handleDelete(req)} style={{ background: 'none', border: 'none', color: '#EC2124', fontSize: 12, cursor: 'pointer', padding: '3px 8px' }}>Delete</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div style={{ marginTop: 16, padding: 16, background: 'rgba(0,0,0,0.2)', border: '1px solid var(--ni-border-soft)', borderRadius: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>{editId ? 'Edit Requirement' : 'New Requirement'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <FormField label="Section #" value={form.sectionNumber} onChange={v => setForm(f => ({ ...f, sectionNumber: v }))} />
            <FormField label="Section title" value={form.sectionTitle} onChange={v => setForm(f => ({ ...f, sectionTitle: v }))} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <FormField label="Title *" value={form.title} onChange={v => setForm(f => ({ ...f, title: v }))} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)', marginBottom: 4 }}>Requirement text</label>
            <textarea
              value={form.requirementText}
              onChange={e => setForm(f => ({ ...f, requirementText: e.target.value }))}
              style={{ width: '100%', minHeight: 70, background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '8px 10px', fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)', marginBottom: 4 }}>Type</label>
              <select value={form.requirementType} onChange={e => setForm(f => ({ ...f, requirementType: e.target.value }))}
                style={{ width: '100%', background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '8px 10px', fontSize: 13 }}>
                <option value="manual">Manual check</option>
                <option value="measurement">Measurement</option>
                <option value="checklist">Checklist</option>
                <option value="evidence">Evidence required</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)', marginBottom: 4 }}>Severity</label>
              <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                style={{ width: '100%', background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '8px 10px', fontSize: 13 }}>
                <option value="Critical">Critical</option>
                <option value="Major">Major</option>
                <option value="Minor">Minor</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--ni-text-soft)' }}>
              <input type="checkbox" checked={form.requiresEvidence} onChange={e => setForm(f => ({ ...f, requiresEvidence: e.target.checked }))} />
              Requires evidence (photo, file, measurement)
            </label>
          </div>
          {reqError && <div style={{ color: '#EC2124', fontSize: 12, marginBottom: 10 }}>{reqError}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleSave} disabled={saving} style={btnStyle('#46BB89', '#000')}>
              {saving ? 'Saving…' : editId ? 'Save changes' : 'Add requirement'}
            </button>
            <button
              onClick={() => { setShowAdd(false); setEditId(null); resetForm(); setReqError(''); }}
              style={{ background: 'none', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text-muted)', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontSize: 13 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ label, value, onChange }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)', marginBottom: 4 }}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ width: '100%', background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '8px 10px', fontSize: 13, boxSizing: 'border-box' }}
      />
    </div>
  );
}
